import { fetchCache } from './storage';
import hashData from '@os/hash';

/**
 * fetch with caching, and an easy way to fetch JSON
 * @module util/cachedFetch
 */

// Dedupe inflight requests. If you try to request something while a fetch is already in progress,
// it will return the same Promise as the request already in progress.
const IN_FLIGHT = {};

// The duration to keep fetch responses in cache, in seconds
const DEFAULT_CACHE_MAX_AGE = 60;

const isGoodResponse = r => (r.ok ? r : Promise.reject(`cloudquote Error ${r.status}`));
const toJSON = r => r.json();


/**
 * Fetch wrapped with max-age caching, saves to dbee until it expires.
 * <p>See <a href="https://www.sitepoint.com/cache-fetched-ajax-requests/">Cache Fetched AJAX Requests</a></p>
 * If caching every single request becomes a memory problem on the client side,
 * maybe use LRU caching of last 25~ requests instead
 * <p>TODO: Implement a shared cached for stock symbols</p>
 * @param {String} url                 the url to fetch. this is also hashed to create a cacheKey
 * @param {Object|Number} options      options for fetch. if options is a number, it is used as the expiry time for this request in cache
 * @param {Number} [options.expiry]    the expiry time for a fetch to stay active in cache, default 300 seconds
 * @memberof util/cachedFetch
 */
function cachedFetch(url, options) {
	let expiry = DEFAULT_CACHE_MAX_AGE;
	if (typeof options === 'number') {
		expiry = options;
		options = undefined;
	}
	else if (typeof options === 'object') {
		expiry = typeof options.expiry === 'number'
			? options.expiry
			: expiry;
	}

	let cacheKey = hashData(url + JSON.stringify(options));

	let cached = fetchCache.get(cacheKey) || {};
	let { data = null, ts = null } = cached;

	if (data !== null && ts !== null) {
		let age = (Date.now() - ts) / 1000;
		if (age < expiry) {
			let response = typeof Blob !== 'undefined'
				? new Response(new Blob([data]))
				: {
					ok: true,
					status: 200,
					text: () => data,
					json: () => JSON.parse(data)
				};
			return Promise.resolve(response);
		}

		fetchCache.remove(cacheKey);
	}

	
	return fetch(url, options)
		.then((response) => {
			if (response.status === 200) {

				// only store json and text
				let ct = response.headers.get('Content-Type');
				if (ct && (ct.match(/application\/json/i) || ct.match(/text\//i))) {

					// clone response because responses can only be consumed once
					response.clone().text().then((data) => {

						// store the hashed url with the response data and current timestamp
						fetchCache.set(cacheKey, {
							data,
							ts: Date.now()
						});
					});
				}
			}
			return response;
		});
}

/**
 * Use cachedFetch to get a URL, if the response is OK then resolves with the JSON response
 * @param {String} url           a url to fetch
 * @param {Function} callback    a callback for handling response data
 * @returns {Promise}            a Promise which resolves when the request completes, rejects if the response status !== 200
 * @memberof util/cachedFetch
 */
export default function cachedGetJSON(url, options) {
	let cacheKey = hashData(url + JSON.stringify(options));
	if (IN_FLIGHT[cacheKey]) { return IN_FLIGHT[cacheKey]; }

	return IN_FLIGHT[cacheKey] = cachedFetch(url, options)
		.then((data) => { delete IN_FLIGHT[cacheKey]; return data; })
		.then(isGoodResponse)
		.then(toJSON)
		.catch((err) => { delete IN_FLIGHT[cacheKey]; return Promise.reject(err); });

}
